import { z } from "zod"

export const githubIdSchema = z.number().int().positive().safe()
// Match the backend-signed context grammar across the complete string.
export const githubContextIdSchema = z
  .string()
  .min(1)
  .max(128)
  .refine(
    (value) => !/[^A-Za-z0-9_.:-]/.test(value),
    "Invalid signed context identifier",
  )
export const githubRevisionSchema = z.number().int().positive().nullable()
export const githubInstallationSchema = z.object({
  app_id: githubIdSchema,
  installation_id: githubIdSchema,
  account_id: githubIdSchema,
})
export const githubRepositorySchema = z.object({
  repository_id: githubIdSchema,
  account_id: githubIdSchema,
  full_name: z.string().min(1).max(300),
  private: z.boolean(),
  default_branch: z.string().min(1).max(255),
})
export const githubBindingSchema = z
  .object({
    agent_id: githubContextIdSchema,
    workspace_id: githubContextIdSchema,
    binding_revision: z.number().int().positive(),
    active: z.boolean(),
    installation: githubInstallationSchema,
    repository: githubRepositorySchema,
    bound_by: z.string().min(1),
    updated_at: z.string().datetime({ offset: true }),
  })
  .refine(
    (value) => value.installation.account_id === value.repository.account_id,
  )
export const githubBindingsSchema = z.object({
  workspace_id: githubContextIdSchema,
  bindings: z.array(githubBindingSchema).max(1000),
})
export const githubBindingResponseSchema = z.object({
  binding: githubBindingSchema,
})
export const githubRedirectSchema = z.object({
  status: z.literal("redirect"),
  state: z.string().min(1).max(512),
  authorization_url: z.string().url().max(4000),
  expires_at: z.string().datetime({ offset: true }),
})
export const githubSelectionSchema = z.object({
  status: z.literal("select_repository"),
  selection_id: z.string().min(1).max(512),
  expires_at: z.string().datetime({ offset: true }),
  agent_id: githubContextIdSchema,
  installation: githubInstallationSchema,
  repositories: z.array(githubRepositorySchema).max(1000),
})
export const githubCallbackSchema = z.discriminatedUnion("status", [
  githubRedirectSchema,
  githubSelectionSchema,
])
export const githubBrowserCallbackSchema = z.union([
  githubRedirectSchema,
  githubSelectionSchema.extend({
    expected_binding_revision: githubRevisionSchema,
  }),
])
export type GithubBinding = z.infer<typeof githubBindingSchema>
export type GithubSelection = z.infer<typeof githubSelectionSchema> & {
  expected_binding_revision: number | null
}

export function isGithubAuthorizationUrl(url: string, state: string): boolean {
  const parsed = new URL(url)
  return (
    parsed.origin === "https://github.com" &&
    !parsed.username &&
    !parsed.password &&
    ["/apps/coval-ai/installations/new", "/login/oauth/authorize"].includes(
      parsed.pathname,
    ) &&
    parsed.searchParams.get("state") === state
  )
}
